import {
  default as superAgentRequest,
  ProgressEvent,
  SuperAgentStatic,
  SuperAgentRequest,
  Response,
} from 'superagent';

import {getGlobalRequestHandlers} from './utilities/globalRequestHandlers';

export type RequestHeaders = {
  [header: string]: string;
};
export type RequestHeadersHandler = (headers: RequestHeaders) => RequestHeaders;

export type ConfigureAgentHandler = (
  agent: SuperAgentStatic
) => SuperAgentStatic;

export type ConfigureRequestHandler = (
  agent: SuperAgentRequest
) => SuperAgentRequest;

export type CallbackHandler = (err: any, res?: Response) => void;

export type AreAllDocumentsMovableToAnotherCasusResponse = {
  areAllDocumentsMovable: boolean;
};

export type CasusInResponse = {
  displayNameWithClient: string;
  id: string;
};

export type CheckNameAvailabilityResponse = {
  available: boolean;
};

export type CopyDocumentResponse = {
  createdDocument: DocumentInResponse;
};

export type CreateDocumentFromTemporaryDocumentParams = {
  documentTitle: string;
  logDescription?: string;
  temporaryDocumentId: string;
};

export type CreateDocumentResponse = {
  createdDocument: DocumentInResponse;
};

export type CreateEmailsParams = {
  documentIds: Array<string>;
};

export type CreateFolderResponse = {
  createdFolderId: string;
};

export type CreateFromTemplateParams = {
  targetCasusId: string;
  targetFolderId: string;
  title: string;
};

export type CreateFromTemplateResponse = {
  createdDocument: DocumentInResponse;
};

export type CreateMultiplePdfsParams = {
  documentIds: Array<string>;
};

export type CreateTemporaryDocumentFromTemplateParams = {
  async: boolean;
  casusId?: string;
  datePlaceholders: Array<DatePlaceholderInParams>;
  emailTemplateParams?: EmailFromTemplateCreationInParams;
  entityPlaceholders: Array<EntityPlaceholderInParams>;
  locale: string;
  numberPlaceholders: Array<NumberPlaceholderInParams>;
  reportFormat?: 'PDF' | 'WORD' | 'EXCEL';
  stringPlaceholders: Array<StringPlaceholderInParams>;
  templateFileId: string;
  templateId: string;
};

export type CreateTemporaryDocumentResponse = {
  temporaryDocumentId: string;
};

export type DatePlaceholderInParams = {
  key: string;
  type: 'DATE';
  value?: string;
};

export type DefaultValueInResponse = {
  entityId?: string;
  options?: Array<ValueOptionInResponse>;
  value: string;
};

export type DeleteDocumentsParams = {
  documentIds: Array<string>;
};

export type DeletedDocumentInResponse = {
  casusId: string;
  createdAt: string;
  deletedAt: string;
  deletedBy: UserInResponse;
  description: string;
  documentNumber: string;
  extension?: string;
  fileName: string;
  hasOlderVersions: boolean;
  id: string;
  latestDocumentVersion: DocumentVersionInResponse;
  links: DocumentLinksInResponse;
  originalFolderPath: Array<string>;
  owner: UserInResponse;
  signable: boolean;
  tagIds: Array<string>;
  templateTargetFileExtension?: string;
  title: string;
};

export type DigitalSigningErrorInfo = {
  moreInfoLink?: string;
  text: string;
};

export type DigitalSigningParams = {
  documentIds: Array<string>;
};

export type DocumentActions = {
  clientSideComparable: boolean;
  clientSidePdfGeneration?: 'WORD' | 'EXCEL' | 'POWERPOINT';
  serverSidePdfGeneration: boolean;
};

export type DocumentCopyParams = {
  newCasusId: string;
  newFolderId: string;
  titleOfDuplicatedDocument: string;
};

export type DocumentDigitalSigningCondition = {
  conditionType:
    | 'SigningUserHasNoCredentialsCondition'
    | 'DocumentSigningPrivasphereRequestFailedCondition';
};

export type DocumentDuplicateParams = {
  titleOfDuplicatedDocument: string;
};

export type DocumentInResponse = {
  casusId: string;
  createdAt: string;
  description?: string;
  documentActions: DocumentActions;
  documentNumber: string;
  extension?: string;
  fileName: string;
  folderId: string;
  folderPath: Array<string>;
  hasOlderVersions: boolean;
  id: string;
  latestDocumentVersion: DocumentVersionInResponse;
  links: DocumentLinksInResponse;
  owner: UserInResponse;
  signable: boolean;
  tagIds: Array<string>;
  templateTargetFileExtension?: string;
  title: string;
};

export type DocumentInUseCondition = {
  conditionType: 'DocumentInUseCondition';
  documentId: string;
};

export type DocumentInUseOrLockedCondition = {
  conditionType: 'DocumentInUseCondition' | 'DocumentLockedCondition';
};

export type DocumentLinksInResponse = {
  linkedInvoiceReminderId?: string;
  linkedToAnObligation: boolean;
  originatingInvoiceContainerId?: string;
};

export type DocumentLock = {
  casus: CasusInResponse;
  documentId: string;
  documentName: string;
  folderId: string;
  folderName?: string;
  lockedAt: string;
  user: UserInResponse;
};

export type DocumentLockState = {
  casusId?: string;
  documentId: string;
  fileName?: string;
  lockState: 'LOCKED' | 'NOT_LOCKED' | 'NO_LOCK_REQUIRED';
  sanitizedFilename?: string;
};

export type DocumentLockedCondition = {
  conditionType: 'DocumentLockedCondition';
  lockedDocuments: Array<string>;
};

export type DocumentRenameParams = {
  title: string;
};

export type DocumentRestoreParams = {
  targetFolderId?: string;
};

export type DocumentSelection = {
  casusId: string;
  documentSelection: Array<string>;
};

export type DocumentSigningCondition = {
  conditionType:
    | 'DocumentsNotSignableCondition'
    | 'IncorrectCertificatePasswordCondition';
};

export type DocumentSigningPrivasphereRequestFailedCondition = {
  conditionType: 'DocumentSigningPrivasphereRequestFailedCondition';
  errors: Array<DigitalSigningErrorInfo>;
};

export type DocumentVersionInResponse = {
  displayName: string;
  fileSizeInBytes: number;
  id: string;
  lastModifiedAt: string;
  lastModifiedBy: UserInResponse;
  linkedObligations: Array<LinkedObligationInResponse>;
  signed: boolean;
  type:
    | 'EMAIL_DOCUMENT'
    | 'EXCEL_DOCUMENT'
    | 'POWERPOINT_DOCUMENT'
    | 'PDF_DOCUMENT'
    | 'WORD_DOCUMENT'
    | 'IMAGE_DOCUMENT'
    | 'TEXT_DOCUMENT'
    | 'RTF_DOCUMENT'
    | 'ZIP_DOCUMENT'
    | 'UNKNOWN_DOCUMENT';
};

export type DocumentsNotSignableCondition = {
  conditionType: 'DocumentsNotSignableCondition';
  notSignableDocumentNames: Array<string>;
};

export type DocumentsRestoreParams = {
  deletedDocumentsIds: Array<string>;
  targetFolderId?: string;
};

export type DuplicateDocumentResponse = {
  createdDocument: DocumentInResponse;
};

export type EmailAddress = {
  emailAddress: string;
  name?: string;
};

export type EmailContact = {
  displayName: string;
  emailAddress: string;
};

export type EmailFromTemplateCreationInParams = {
  attachments?: DocumentSelection;
  recipients: EmailRecipientsInParams;
};

export type EmailRecipientsInParams = {
  bcc: Array<EmailAddress>;
  cc: Array<EmailAddress>;
  to: Array<EmailAddress>;
};

export type EntityPlaceholderInParams = {
  identifier: Array<string>;
  key: string;
  type:
    | 'CONTACT'
    | 'COLLABORATOR'
    | 'INVOLVED_PARTY'
    | 'CASUS'
    | 'CLIENT'
    | 'USER'
    | 'OFFICE_LOCATION'
    | 'CLIENT_MONEY_BANK_ACCOUNT'
    | 'SPECIALITY_FIELD';
};

export type EnumOptionInResponse = {
  categoryName?: string;
  displayName: string;
  value: string;
};

export type FolderCreateParams = {
  name: string;
};

export type FolderInHierarchy = {
  folder: FolderInResponse;
  subFolderInThePathToTheSelectedFolder?: FolderInHierarchy;
  subFolders: Array<FolderInResponse>;
};

export type FolderInResponse = {
  hasSubFolders: boolean;
  id: string;
  name: string;
};

export type FolderRenameParams = {
  name: string;
};

export type GetDeletedDocumentsResponse = {
  deletedDocuments: PageDeletedDocumentInResponse;
};

export type GetDocumentResponse = {
  document: DocumentInResponse;
};

export type GetDocumentsResponse = {
  documents: PageDocumentInResponse;
};

export type GetFolderContentTypesResponse = {
  containsDocuments: boolean;
  containsEmails: boolean;
};

export type GetFolderHierarchyResponse = {
  rootFolder: FolderInHierarchy;
};

export type GetFoldersResponse = {
  folders: Array<FolderInResponse>;
};

export type GetInactiveDocumentVersionsResponse = {
  inactiveDocumentVersions: Array<DocumentVersionInResponse>;
};

export type GetLockedDocumentByIdResponse = {
  document: LockedDocumentInResponse;
};

export type GetLockedDocumentsResponse = {
  lockedDocuments: Array<LockedDocumentInResponse>;
};

export type GetNumberOfDocumentsInFolderResponse = {
  numberOfDocuments: number;
};

export type GetOverridableTemplatePlaceholdersResponse = {
  pagedPlaceholders: Array<PageInResponse>;
};

export type GetTemplatesResponse = {
  templates: Array<TemplateInResponse>;
};

export type IncorrectCertificatePasswordCondition = {
  conditionType: 'IncorrectCertificatePasswordCondition';
};

export type IsFolderMovableToAnotherCasusResponse = {
  folderMovable: boolean;
};

export type LinkedObligationInResponse = {
  note: string;
  obligationId: string;
  obligationVersionId: string;
};

export type LockedDocumentInResponse = {
  casusDisplayName: string;
  casusId: string;
  createdAt: string;
  createdBy: UserInResponse;
  displayName: string;
  documentId: string;
  documentType:
    | 'EMAIL_DOCUMENT'
    | 'EXCEL_DOCUMENT'
    | 'POWERPOINT_DOCUMENT'
    | 'PDF_DOCUMENT'
    | 'WORD_DOCUMENT'
    | 'IMAGE_DOCUMENT'
    | 'TEXT_DOCUMENT'
    | 'RTF_DOCUMENT'
    | 'ZIP_DOCUMENT'
    | 'UNKNOWN_DOCUMENT';
  fileName: string;
  inUseBy: UserInResponse;
  lastModifiedAt: string;
  lastModifiedBy: UserInResponse;
};

export type MoveDocumentsParams = {
  documentsToMove: Array<string>;
  newCasusId: string;
  newFolderId: string;
};

export type MoveFolderParams = {
  newCasusId: string;
  newParentFolderId: string;
};

export type MoveFolderResponse = {
  foldersOfDestinationFolderAfterMovement: Array<FolderInResponse>;
};

export type NonUniqueItemNameCondition = {
  conditionType: 'NonUniqueItemNameCondition';
  duplicateDocumentOrFolderName: string;
};

export type NotConvertibleToEmailCondition = {
  conditionType: 'NotConvertibleToEmailCondition';
  notConvertibleDocumentNames: Array<string>;
};

export type NotConvertibleToPdfCondition = {
  conditionType: 'NotConvertibleToPdfCondition';
  notConvertibleDocumentNames: Array<string>;
};

export type NumberPlaceholderInParams = {
  key: string;
  type: 'NUMBER';
  value?: number;
};

export type PageDeletedDocumentInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemDeletedDocumentInResponse>;
};

export type PageDocumentInResponse = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemDocumentInResponse>;
};

export type PageEmailContact = {
  hasMoreAfter: boolean;
  hasMoreBefore: boolean;
  items: Array<PageItemEmailContact>;
};

export type PageInResponse = {
  key: string;
  label: string;
  placeholders: Array<PlaceholderInResponse>;
};

export type PageItemDeletedDocumentInResponse = {
  cursor: string;
  item: DeletedDocumentInResponse;
};

export type PageItemDocumentInResponse = {
  cursor: string;
  item: DocumentInResponse;
};

export type PageItemEmailContact = {
  cursor: string;
  item: EmailContact;
};

export type PlaceholderInResponse = {
  defaultValue: Array<DefaultValueInResponse>;
  editFieldType?: 'TEXT' | 'TEXTAREA';
  enumOptions?: Array<EnumOptionInResponse>;
  key: string;
  label: string;
  multiple: boolean;
  optional: boolean;
  placeholders?: Array<PlaceholderInResponse>;
  type:
    | 'ENUM'
    | 'USER_AND_ROLE'
    | 'USER_AND_CONTACT_ROLE'
    | 'BILLABLE_CATEGORY'
    | 'LOCALE'
    | 'TEXT'
    | 'TEXTAREA'
    | 'NUMBER'
    | 'DATE'
    | 'CONTACT'
    | 'COLLABORATOR'
    | 'INVOLVED_PARTY'
    | 'CASUS'
    | 'CLIENT'
    | 'USER'
    | 'OFFICE_LOCATION'
    | 'CLIENT_MONEY_BANK_ACCOUNT'
    | 'SPECIALITY_FIELD'
    | 'EMAIL'
    | 'PHONE'
    | 'ADDRESS'
    | 'LINK'
    | 'GROUP';
};

export type RelatedAddressContactInResponse = {
  label: string;
  roleId: string;
};

export type RenameDocumentResponse = {
  documentAfterRenaming: DocumentInResponse;
};

export type RenameFolderResponse = {
  foldersOfParentFolderAfterRename: Array<FolderInResponse>;
};

export type RestoreDocumentResponse = {
  folderPathToRestoredDocument: Array<string>;
};

export type RestoreDocumentsResponse = {
  folderPathsToRestoredDocuments: Array<Array<string>>;
};

export type SearchEmailAddressesResponse = {
  emails: PageEmailContact;
};

export type SendByMailParams = {
  documentsToShare: Array<string>;
  fileFormat: 'PDF' | 'ORIGINAL';
  message?: string;
  sendToMailAddress: string;
};

export type SetDescriptionParams = {
  userSetDescription?: string;
};

export type ShareWithUserParams = {
  documentsToShare: Array<string>;
  message?: string;
  shareWithUserId: string;
};

export type SharedDocumentsResponse = {
  sharedDocumentIds: Array<string>;
};

export type SignDocumentsParams = {
  certificatePassword?: string;
  documentsToSign: Array<string>;
};

export type SigningUserHasNoCredentialsCondition = {
  conditionType: 'SigningUserHasNoCredentialsCondition';
};

export type StringPlaceholderInParams = {
  identifier?: string;
  key: string;
  type:
    | 'TEXT'
    | 'TEXTAREA'
    | 'ENUM'
    | 'USER_AND_ROLE'
    | 'USER_AND_CONTACT_ROLE'
    | 'BILLABLE_CATEGORY'
    | 'LOCALE'
    | 'EMAIL'
    | 'PHONE'
    | 'ADDRESS'
    | 'LINK';
  value: Array<string>;
};

export type TagAssignmentParams = {
  entityId: string;
  tagIds: Array<string>;
};

export type TemplateFileInResponse = {
  id: string;
  lastModifiedAt: string;
  lastModifiedBy: UserInResponse;
  locale: string;
  title: string;
  type: 'WORD' | 'EXCEL' | 'POWERPOINT' | 'EMAIL' | 'ZIP';
};

export type TemplateInResponse = {
  id: string;
  templateFiles: Array<TemplateFileInResponse>;
  title: string;
};

export type TemporaryDocumentInResponse = {
  displayName: string;
  extension?: string;
  id: string;
  title: string;
  type:
    | 'EMAIL_DOCUMENT'
    | 'EXCEL_DOCUMENT'
    | 'POWERPOINT_DOCUMENT'
    | 'PDF_DOCUMENT'
    | 'WORD_DOCUMENT'
    | 'IMAGE_DOCUMENT'
    | 'TEXT_DOCUMENT'
    | 'RTF_DOCUMENT'
    | 'ZIP_DOCUMENT'
    | 'UNKNOWN_DOCUMENT';
};

export type TemporaryDocumentRenameParams = {
  title: string;
};

export type TemporaryDocumentResponse = {
  temporaryDocument: TemporaryDocumentInResponse;
};

export type Unit = {};

export type UpdateDocumentResponse = {
  updatedDocument: DocumentInResponse;
};

export type UploadedDocumentInParams = {
  errorMessage?: string;
  uploadWasSuccessful: boolean;
};

export type UploadedDocumentsParams = {
  uploadedDocuments: Array<UploadedDocumentInParams>;
};

export type UserInResponse = {
  active: boolean;
  displayName: string;
  id: string;
  initialLetters: string;
};

export type ValueOptionInResponse = {
  classificationId?: string;
  displayName: string;
  entityId: string;
  fieldValue: string;
  relatedAddressContact?: RelatedAddressContactInResponse;
};

export type ZipDocumentsParams = {
  destinationFolderId: string;
  documentIds: Array<string>;
};

export type ZipFileExtractionParams = {
  targetFolderId: string;
  zipFileId: string;
};

export type ZipFilesExtractionParams = {
  zipFiles: Array<ZipFileExtractionParams>;
};

export type Response_downloadDeletedDocumentPdf_200 = {};

export type Response_downloadDeletedDocumentThumbnail_200 = {};

export type Response_downloadDocumentVersion_200 = {};

export type Response_downloadDocumentVersionPdf_200 = {};

export type Response_download_200 = {};

export type Response_open_200 = string;

export type Response_downloadPdf_200 = {};

export type Response_downloadThumbnail_200 = {};

export type Response_getAllDocumentLocks_200 = Array<DocumentLock>;

export type Response_getDocumentPreview_200 = {};

export type Response_getDocumentsLockState_200 = Array<DocumentLockState>;

export type Response_getDependentPlaceholderValues_200 =
  Array<DefaultValueInResponse>;

export type Response_downloadTemporaryDocument_200 = {};

export type Response_downloadTemporaryDocumentPdf_200 = {};

export interface ResponseWithBody<S extends number, T> extends Response {
  status: S;
  body: T;
}

export type QueryParameters = {[param: string]: any};

export interface CommonRequestOptions {
  $queryParameters?: QueryParameters;
  $domain?: string;
  $path?: string | ((path: string) => string);
  $progressCallback?: (arg0: ProgressEvent) => void;
  $retries?: number;
  $timeout?: number;
  $deadline?: number;
  $signal?: AbortSignal;
}

type SuperAgentError = {status: number};
const isSuperAgentError = (error: unknown): error is SuperAgentError =>
  Boolean((error as SuperAgentError).status);

/*
 *
 * @class Backend
 * @param {(string)} [domainOrOptions] - The project domain.
 */
export class Backend {
  private domain: string = '';
  private onUnauthorizedHandler?: CallbackHandler;
  private errorHandlers: CallbackHandler[] = [];
  private requestHeadersHandler?: RequestHeadersHandler;
  private configureAgentHandler?: ConfigureAgentHandler;
  private configureRequestHandler?: ConfigureRequestHandler;

  public constructor(domain?: string, onUnauthorizedHandler?: CallbackHandler) {
    if (domain) {
      this.domain = domain;
    }

    if (onUnauthorizedHandler) {
      this.onUnauthorizedHandler = onUnauthorizedHandler;
    }

    this.deleteAllDocumentsFromBinURL =
      this.deleteAllDocumentsFromBinURL.bind(this);
    this.deleteAllDocumentsFromBin = this.deleteAllDocumentsFromBin.bind(this);
    this.getDeletedDocumentsURL = this.getDeletedDocumentsURL.bind(this);
    this.getDeletedDocuments = this.getDeletedDocuments.bind(this);
    this.restoreDocumentsURL = this.restoreDocumentsURL.bind(this);
    this.restoreDocuments = this.restoreDocuments.bind(this);
    this.restoreDocumentURL = this.restoreDocumentURL.bind(this);
    this.restoreDocument = this.restoreDocument.bind(this);
    this.downloadDeletedDocumentPdfURL =
      this.downloadDeletedDocumentPdfURL.bind(this);
    this.downloadDeletedDocumentPdf =
      this.downloadDeletedDocumentPdf.bind(this);
    this.downloadDeletedDocumentThumbnailURL =
      this.downloadDeletedDocumentThumbnailURL.bind(this);
    this.downloadDeletedDocumentThumbnail =
      this.downloadDeletedDocumentThumbnail.bind(this);
    this.deleteDocumentsURL = this.deleteDocumentsURL.bind(this);
    this.deleteDocuments = this.deleteDocuments.bind(this);
    this.getDocumentsURL = this.getDocumentsURL.bind(this);
    this.getDocuments = this.getDocuments.bind(this);
    this.areAllDocumentsMovableToAnotherCasusURL =
      this.areAllDocumentsMovableToAnotherCasusURL.bind(this);
    this.areAllDocumentsMovableToAnotherCasus =
      this.areAllDocumentsMovableToAnotherCasus.bind(this);
    this.createEmailsURL = this.createEmailsURL.bind(this);
    this.createEmails = this.createEmails.bind(this);
    this.createMultiplePdfsURL = this.createMultiplePdfsURL.bind(this);
    this.createMultiplePdfs = this.createMultiplePdfs.bind(this);
    this.digitallySignDocumentURL = this.digitallySignDocumentURL.bind(this);
    this.digitallySignDocument = this.digitallySignDocument.bind(this);
    this.extractZipFilesURL = this.extractZipFilesURL.bind(this);
    this.extractZipFiles = this.extractZipFiles.bind(this);
    this.moveDocumentsURL = this.moveDocumentsURL.bind(this);
    this.moveDocuments = this.moveDocuments.bind(this);
    this.sendByMailURL = this.sendByMailURL.bind(this);
    this.sendByMail = this.sendByMail.bind(this);
    this.shareWithUserURL = this.shareWithUserURL.bind(this);
    this.shareWithUser = this.shareWithUser.bind(this);
    this.signDocumentsURL = this.signDocumentsURL.bind(this);
    this.signDocuments = this.signDocuments.bind(this);
    this.setTagsURL = this.setTagsURL.bind(this);
    this.setTags = this.setTags.bind(this);
    this.zipDocumentsURL = this.zipDocumentsURL.bind(this);
    this.zipDocuments = this.zipDocuments.bind(this);
    this.renameDocumentURL = this.renameDocumentURL.bind(this);
    this.renameDocument = this.renameDocument.bind(this);
    this.copyDocumentURL = this.copyDocumentURL.bind(this);
    this.copyDocument = this.copyDocument.bind(this);
    this.createFromTemplateURL = this.createFromTemplateURL.bind(this);
    this.createFromTemplate = this.createFromTemplate.bind(this);
    this.setDescriptionURL = this.setDescriptionURL.bind(this);
    this.setDescription = this.setDescription.bind(this);
    this.downloadDocumentVersionURL =
      this.downloadDocumentVersionURL.bind(this);
    this.downloadDocumentVersion = this.downloadDocumentVersion.bind(this);
    this.downloadDocumentVersionPdfURL =
      this.downloadDocumentVersionPdfURL.bind(this);
    this.downloadDocumentVersionPdf =
      this.downloadDocumentVersionPdf.bind(this);
    this.downloadURL = this.downloadURL.bind(this);
    this.download = this.download.bind(this);
    this.duplicateDocumentURL = this.duplicateDocumentURL.bind(this);
    this.duplicateDocument = this.duplicateDocument.bind(this);
    this.getInactiveDocumentVersionsURL =
      this.getInactiveDocumentVersionsURL.bind(this);
    this.getInactiveDocumentVersions =
      this.getInactiveDocumentVersions.bind(this);
    this.lockDocumentURL = this.lockDocumentURL.bind(this);
    this.lockDocument = this.lockDocument.bind(this);
    this.openURL = this.openURL.bind(this);
    this.open = this.open.bind(this);
    this.downloadPdfURL = this.downloadPdfURL.bind(this);
    this.downloadPdf = this.downloadPdf.bind(this);
    this.downloadThumbnailURL = this.downloadThumbnailURL.bind(this);
    this.downloadThumbnail = this.downloadThumbnail.bind(this);
    this.unlockDocumentURL = this.unlockDocumentURL.bind(this);
    this.unlockDocument = this.unlockDocument.bind(this);
    this.uploadNewVersionURL = this.uploadNewVersionURL.bind(this);
    this.uploadNewVersion = this.uploadNewVersion.bind(this);
    this.getFolderHierarchyURL = this.getFolderHierarchyURL.bind(this);
    this.getFolderHierarchy = this.getFolderHierarchy.bind(this);
    this.getFolderHierarchyForDocumentURL =
      this.getFolderHierarchyForDocumentURL.bind(this);
    this.getFolderHierarchyForDocument =
      this.getFolderHierarchyForDocument.bind(this);
    this.deleteFolderURL = this.deleteFolderURL.bind(this);
    this.deleteFolder = this.deleteFolder.bind(this);
    this.renameFolderURL = this.renameFolderURL.bind(this);
    this.renameFolder = this.renameFolder.bind(this);
    this.getFolderContentTypesURL = this.getFolderContentTypesURL.bind(this);
    this.getFolderContentTypes = this.getFolderContentTypes.bind(this);
    this.checkDocumentNameAvailabilityURL =
      this.checkDocumentNameAvailabilityURL.bind(this);
    this.checkDocumentNameAvailability =
      this.checkDocumentNameAvailability.bind(this);
    this.createDocumentURL = this.createDocumentURL.bind(this);
    this.createDocument = this.createDocument.bind(this);
    this.getNumberOfDocumentsInFolderURL =
      this.getNumberOfDocumentsInFolderURL.bind(this);
    this.getNumberOfDocumentsInFolder =
      this.getNumberOfDocumentsInFolder.bind(this);
    this.createDocumentFromTemporaryDocumentURL =
      this.createDocumentFromTemporaryDocumentURL.bind(this);
    this.createDocumentFromTemporaryDocument =
      this.createDocumentFromTemporaryDocument.bind(this);
    this.importDocumentURL = this.importDocumentURL.bind(this);
    this.importDocument = this.importDocument.bind(this);
    this.getDocumentURL = this.getDocumentURL.bind(this);
    this.getDocument = this.getDocument.bind(this);
    this.checkFolderNameAvailabilityURL =
      this.checkFolderNameAvailabilityURL.bind(this);
    this.checkFolderNameAvailability =
      this.checkFolderNameAvailability.bind(this);
    this.getFoldersURL = this.getFoldersURL.bind(this);
    this.getFolders = this.getFolders.bind(this);
    this.createFolderURL = this.createFolderURL.bind(this);
    this.createFolder = this.createFolder.bind(this);
    this.importFolderURL = this.importFolderURL.bind(this);
    this.importFolder = this.importFolder.bind(this);
    this.isFolderMovableToAnotherCasusURL =
      this.isFolderMovableToAnotherCasusURL.bind(this);
    this.isFolderMovableToAnotherCasus =
      this.isFolderMovableToAnotherCasus.bind(this);
    this.moveFolderURL = this.moveFolderURL.bind(this);
    this.moveFolder = this.moveFolder.bind(this);
    this.documentUploadFinishedURL = this.documentUploadFinishedURL.bind(this);
    this.documentUploadFinished = this.documentUploadFinished.bind(this);
    this.zipFolderURL = this.zipFolderURL.bind(this);
    this.zipFolder = this.zipFolder.bind(this);
    this.getLockedDocumentByIdURL = this.getLockedDocumentByIdURL.bind(this);
    this.getLockedDocumentById = this.getLockedDocumentById.bind(this);
    this.removeDocumentLocksURL = this.removeDocumentLocksURL.bind(this);
    this.removeDocumentLocks = this.removeDocumentLocks.bind(this);
    this.getAllDocumentLocksURL = this.getAllDocumentLocksURL.bind(this);
    this.getAllDocumentLocks = this.getAllDocumentLocks.bind(this);
    this.removeDocumentLockURL = this.removeDocumentLockURL.bind(this);
    this.removeDocumentLock = this.removeDocumentLock.bind(this);
    this.getDocumentTemplatesURL = this.getDocumentTemplatesURL.bind(this);
    this.getDocumentTemplates = this.getDocumentTemplates.bind(this);
    this.lockDocumentWithoutCasusURL =
      this.lockDocumentWithoutCasusURL.bind(this);
    this.lockDocumentWithoutCasus = this.lockDocumentWithoutCasus.bind(this);
    this.getDocumentPreviewURL = this.getDocumentPreviewURL.bind(this);
    this.getDocumentPreview = this.getDocumentPreview.bind(this);
    this.unlockDocumentWithoutCasusURL =
      this.unlockDocumentWithoutCasusURL.bind(this);
    this.unlockDocumentWithoutCasus =
      this.unlockDocumentWithoutCasus.bind(this);
    this.uploadNewVersionWithoutCasusURL =
      this.uploadNewVersionWithoutCasusURL.bind(this);
    this.uploadNewVersionWithoutCasus =
      this.uploadNewVersionWithoutCasus.bind(this);
    this.searchEmailAddressesURL = this.searchEmailAddressesURL.bind(this);
    this.searchEmailAddresses = this.searchEmailAddresses.bind(this);
    this.getEmailTemplatesURL = this.getEmailTemplatesURL.bind(this);
    this.getEmailTemplates = this.getEmailTemplates.bind(this);
    this.getDocumentsLockStateURL = this.getDocumentsLockStateURL.bind(this);
    this.getDocumentsLockState = this.getDocumentsLockState.bind(this);
    this.getLockedDocumentsOfLoggedInUserURL =
      this.getLockedDocumentsOfLoggedInUserURL.bind(this);
    this.getLockedDocumentsOfLoggedInUser =
      this.getLockedDocumentsOfLoggedInUser.bind(this);
    this.getReportTemplatesURL = this.getReportTemplatesURL.bind(this);
    this.getReportTemplates = this.getReportTemplates.bind(this);
    this.getDependentPlaceholderValuesURL =
      this.getDependentPlaceholderValuesURL.bind(this);
    this.getDependentPlaceholderValues =
      this.getDependentPlaceholderValues.bind(this);
    this.getOverridableTemplatePlaceholdersURL =
      this.getOverridableTemplatePlaceholdersURL.bind(this);
    this.getOverridableTemplatePlaceholders =
      this.getOverridableTemplatePlaceholders.bind(this);
    this.createTemporaryDocumentURL =
      this.createTemporaryDocumentURL.bind(this);
    this.createTemporaryDocument = this.createTemporaryDocument.bind(this);
    this.createTemporaryDocumentFromTemplateURL =
      this.createTemporaryDocumentFromTemplateURL.bind(this);
    this.createTemporaryDocumentFromTemplate =
      this.createTemporaryDocumentFromTemplate.bind(this);
    this.deleteTemporaryDocumentURL =
      this.deleteTemporaryDocumentURL.bind(this);
    this.deleteTemporaryDocument = this.deleteTemporaryDocument.bind(this);
    this.downloadTemporaryDocumentURL =
      this.downloadTemporaryDocumentURL.bind(this);
    this.downloadTemporaryDocument = this.downloadTemporaryDocument.bind(this);
    this.replaceTemporaryDocumentURL =
      this.replaceTemporaryDocumentURL.bind(this);
    this.replaceTemporaryDocument = this.replaceTemporaryDocument.bind(this);
    this.renameTemporaryDocumentURL =
      this.renameTemporaryDocumentURL.bind(this);
    this.renameTemporaryDocument = this.renameTemporaryDocument.bind(this);
    this.downloadTemporaryDocumentPdfURL =
      this.downloadTemporaryDocumentPdfURL.bind(this);
    this.downloadTemporaryDocumentPdf =
      this.downloadTemporaryDocumentPdf.bind(this);
    this.replaceSigningCertificateURL =
      this.replaceSigningCertificateURL.bind(this);
    this.replaceSigningCertificate = this.replaceSigningCertificate.bind(this);
  }

  public getDomain(): string {
    return this.domain;
  }

  public addErrorHandler(handler: CallbackHandler): void {
    this.errorHandlers.push(handler);
  }

  public setRequestHeadersHandler(handler: RequestHeadersHandler): void {
    this.requestHeadersHandler = handler;
  }

  public setConfigureAgentHandler(handler: ConfigureAgentHandler): void {
    this.configureAgentHandler = handler;
  }

  public setConfigureRequestHandler(handler: ConfigureRequestHandler): void {
    this.configureRequestHandler = handler;
  }

  public request(
    method: string,
    url: string,
    body: any,
    headers: RequestHeaders,
    form: any,
    reject: CallbackHandler,
    resolve: CallbackHandler,
    progress: CallbackHandler,
    opts: CommonRequestOptions,
    signal?: AbortSignal,
    skipRedirectOnUnauthorized = false
  ): void {
    const agent = this.configureAgentHandler
      ? this.configureAgentHandler(superAgentRequest)
      : superAgentRequest;

    if (process.env.NODE_ENV === 'test') {
      throw new Error(
        'Calling endpoints in test environment should be avoided, this endpoint needs to be mocked: ' +
          url +
          ' [' +
          method +
          ']'
      );
    }

    let request = agent(method, url);
    if (this.configureRequestHandler) {
      request = this.configureRequestHandler(request);
    }

    let processedHeaders = headers;
    if (this.requestHeadersHandler) {
      processedHeaders = this.requestHeadersHandler({...headers});
    }

    if (body) {
      request.send(body);

      if (typeof body === 'object' && !(body.constructor.name === 'Buffer')) {
        processedHeaders['Content-Type'] = 'application/json';
      }
    }

    if (Object.keys(form).length > 0) {
      // do not explicitly set the the content type
      // https://stackoverflow.com/questions/39280438/fetch-missing-boundary-in-multipart-form-data-post#39281156
      delete processedHeaders['content-type'];
      const formData = new FormData();

      Object.keys(form).forEach((key: string) =>
        formData.append(key, form[key])
      );
      request.send(formData);
    }

    request.set(processedHeaders);

    if (opts.$retries && opts.$retries > 0) {
      request.retry(opts.$retries);
    }

    if (
      (opts.$timeout && opts.$timeout > 0) ||
      (opts.$deadline && opts.$deadline > 0)
    ) {
      request.timeout({deadline: opts.$deadline, response: opts.$timeout});
    }

    request
      .on('progress', progress)
      .on('abort', () => reject('Aborted'))
      .ok((res) => res.status <= 400)
      .end((error, response) => {
        // an error will also be emitted for a 4xx and 5xx status code
        // the error object will then have error.status and error.response fields
        // see superagent error handling: https://github.com/visionmedia/superagent/blob/master/docs/index.md#error-handling
        if (error) {
          reject(error);
          if (!skipRedirectOnUnauthorized) {
            this.onUnauthorizedHandler?.(error);
          }
          this.errorHandlers.forEach((handler) => handler(error));
        } else {
          resolve(response);
        }
      });

    if (signal) {
      signal.addEventListener('abort', () => {
        request.abort();
      });
    }
  }

  private static convertParameterCollectionFormat<T>(
    param: T,
    collectionFormat: string | undefined
  ): T | string {
    if (Array.isArray(param) && param.length >= 2) {
      switch (collectionFormat) {
        case 'csv':
          return param.join(',');
        case 'ssv':
          return param.join(' ');
        case 'tsv':
          return param.join('\t');
        case 'pipes':
          return param.join('|');
        default:
          return param;
      }
    }

    return param;
  }

  /*
   *
   * @method
   * @name Backend#deleteAllDocumentsFromBin
   * @param {string} casusId -
   */
  public async deleteAllDocumentsFromBin({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteAllDocumentsFromBinURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteAllDocumentsFromBinURL(
    parameters: {
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/deleted-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDeletedDocuments
   * @param {string} casusId -
   * @param {string} createdTimestampFromInclusive -
   * @param {string} createdTimestampToInclusive -
   * @param {array} deletingUserIds -
   * @param {array} documentTypes -
   * @param {array} ownerIds -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} tagIds -
   */
  public async getDeletedDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      createdTimestampFromInclusive?: string;
      createdTimestampToInclusive?: string;
      deletingUserIds?: Array<string>;
      documentTypes?: Array<
        | 'EMAIL_DOCUMENT'
        | 'EXCEL_DOCUMENT'
        | 'POWERPOINT_DOCUMENT'
        | 'PDF_DOCUMENT'
        | 'WORD_DOCUMENT'
        | 'IMAGE_DOCUMENT'
        | 'TEXT_DOCUMENT'
        | 'RTF_DOCUMENT'
        | 'ZIP_DOCUMENT'
        | 'UNKNOWN_DOCUMENT'
      >;
      ownerIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'TITLE'
        | 'CREATED_AT'
        | 'LAST_MODIFIED_AT'
        | 'DELETED_AT'
        | 'ORIGINAL_FOLDER_PATH'
        | 'FILE_SIZE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      tagIds?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetDeletedDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetDeletedDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetDeletedDocumentsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getDeletedDocumentsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetDeletedDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetDeletedDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetDeletedDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetDeletedDocumentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getDeletedDocumentsURL(
    parameters: {
      casusId: string;
      createdTimestampFromInclusive?: string;
      createdTimestampToInclusive?: string;
      deletingUserIds?: Array<string>;
      documentTypes?: Array<
        | 'EMAIL_DOCUMENT'
        | 'EXCEL_DOCUMENT'
        | 'POWERPOINT_DOCUMENT'
        | 'PDF_DOCUMENT'
        | 'WORD_DOCUMENT'
        | 'IMAGE_DOCUMENT'
        | 'TEXT_DOCUMENT'
        | 'RTF_DOCUMENT'
        | 'ZIP_DOCUMENT'
        | 'UNKNOWN_DOCUMENT'
      >;
      ownerIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        | 'TITLE'
        | 'CREATED_AT'
        | 'LAST_MODIFIED_AT'
        | 'DELETED_AT'
        | 'ORIGINAL_FOLDER_PATH'
        | 'FILE_SIZE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      tagIds?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/deleted-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['createdTimestampFromInclusive'] !== 'undefined') {
      queryParameters['createdTimestampFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdTimestampFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdTimestampToInclusive'] !== 'undefined') {
      queryParameters['createdTimestampToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdTimestampToInclusive'],
          ''
        );
    }

    if (typeof parameters['deletingUserIds'] !== 'undefined') {
      queryParameters['deletingUserIds'] =
        Backend.convertParameterCollectionFormat(
          parameters['deletingUserIds'],
          ''
        );
    }

    if (typeof parameters['documentTypes'] !== 'undefined') {
      queryParameters['documentTypes'] =
        Backend.convertParameterCollectionFormat(
          parameters['documentTypes'],
          ''
        );
    }

    if (typeof parameters['ownerIds'] !== 'undefined') {
      queryParameters['ownerIds'] = Backend.convertParameterCollectionFormat(
        parameters['ownerIds'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['tagIds'] !== 'undefined') {
      queryParameters['tagIds'] = Backend.convertParameterCollectionFormat(
        parameters['tagIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#restoreDocuments
   * @param {} body -
   * @param {string} casusId -
   */
  public async restoreDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DocumentsRestoreParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: RestoreDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, RestoreDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, RestoreDocumentsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.restoreDocumentsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, RestoreDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          RestoreDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as RestoreDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, RestoreDocumentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public restoreDocumentsURL(
    parameters: {
      body: DocumentsRestoreParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/deleted-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#restoreDocument
   * @param {} body -
   * @param {string} casusId -
   * @param {string} deletedDocumentId -
   */
  public async restoreDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DocumentRestoreParams;
      casusId: string;
      deletedDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: RestoreDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, RestoreDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, RestoreDocumentResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['deletedDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: deletedDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.restoreDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, RestoreDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          RestoreDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as RestoreDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, RestoreDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public restoreDocumentURL(
    parameters: {
      body: DocumentRestoreParams;
      casusId: string;
      deletedDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/deleted-documents/{deletedDocumentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{deletedDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['deletedDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadDeletedDocumentPdf
   * @param {string} casusId -
   * @param {string} deletedDocumentId -
   */
  public async downloadDeletedDocumentPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      deletedDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadDeletedDocumentPdf_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadDeletedDocumentPdf_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadDeletedDocumentPdf_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'application/pdf';

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['deletedDocumentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: deletedDocumentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadDeletedDocumentPdfURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadDeletedDocumentPdf_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadDeletedDocumentPdf_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadDeletedDocumentPdf_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadDeletedDocumentPdf_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadDeletedDocumentPdfURL(
    parameters: {
      casusId: string;
      deletedDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/deleted-documents/{deletedDocumentId}/pdf/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{deletedDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['deletedDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadDeletedDocumentThumbnail
   * @param {string} casusId -
   * @param {string} deletedDocumentId -
   */
  public async downloadDeletedDocumentThumbnail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      deletedDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadDeletedDocumentThumbnail_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadDeletedDocumentThumbnail_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadDeletedDocumentThumbnail_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'image/png';

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['deletedDocumentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: deletedDocumentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadDeletedDocumentThumbnailURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      Response_downloadDeletedDocumentThumbnail_200
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadDeletedDocumentThumbnail_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadDeletedDocumentThumbnail_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadDeletedDocumentThumbnail_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadDeletedDocumentThumbnailURL(
    parameters: {
      casusId: string;
      deletedDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/deleted-documents/{deletedDocumentId}/thumbnail/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{deletedDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['deletedDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteDocuments
   * @param {} body -
   * @param {string} casusId -
   */
  public async deleteDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DeleteDocumentsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Unit;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, Unit>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, Unit>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteDocumentsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, Unit>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, Unit>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Unit,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, Unit>
        | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteDocumentsURL(
    parameters: {
      body: DeleteDocumentsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDocuments
   * @param {string} casusId -
   * @param {string} createdTimestampFromInclusive -
   * @param {string} createdTimestampToInclusive -
   * @param {array} documentTypes -
   * @param {string} folderId -
   * @param {array} ownerIds -
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   * @param {array} sortFields -
   * @param {array} sortOrders -
   * @param {array} tagIds -
   */
  public async getDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      createdTimestampFromInclusive?: string;
      createdTimestampToInclusive?: string;
      documentTypes?: Array<
        | 'EMAIL_DOCUMENT'
        | 'EXCEL_DOCUMENT'
        | 'POWERPOINT_DOCUMENT'
        | 'PDF_DOCUMENT'
        | 'WORD_DOCUMENT'
        | 'IMAGE_DOCUMENT'
        | 'TEXT_DOCUMENT'
        | 'RTF_DOCUMENT'
        | 'ZIP_DOCUMENT'
        | 'UNKNOWN_DOCUMENT'
      >;
      folderId?: string;
      ownerIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        'TITLE' | 'CREATED_AT' | 'LAST_MODIFIED_AT' | 'FILE_SIZE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      tagIds?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetDocumentsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['paginationDirection'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationDirection'));
          return;
        }

        if (typeof parameters['paginationMode'] === 'undefined') {
          reject(new Error('Missing required  parameter: paginationMode'));
          return;
        }

        if (typeof parameters['sortFields'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortFields'));
          return;
        }

        if (typeof parameters['sortOrders'] === 'undefined') {
          reject(new Error('Missing required  parameter: sortOrders'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getDocumentsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetDocumentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getDocumentsURL(
    parameters: {
      casusId: string;
      createdTimestampFromInclusive?: string;
      createdTimestampToInclusive?: string;
      documentTypes?: Array<
        | 'EMAIL_DOCUMENT'
        | 'EXCEL_DOCUMENT'
        | 'POWERPOINT_DOCUMENT'
        | 'PDF_DOCUMENT'
        | 'WORD_DOCUMENT'
        | 'IMAGE_DOCUMENT'
        | 'TEXT_DOCUMENT'
        | 'RTF_DOCUMENT'
        | 'ZIP_DOCUMENT'
        | 'UNKNOWN_DOCUMENT'
      >;
      folderId?: string;
      ownerIds?: Array<string>;
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
      sortFields: Array<
        'TITLE' | 'CREATED_AT' | 'LAST_MODIFIED_AT' | 'FILE_SIZE'
      >;
      sortOrders: Array<'ASC' | 'DESC'>;
      tagIds?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['createdTimestampFromInclusive'] !== 'undefined') {
      queryParameters['createdTimestampFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdTimestampFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdTimestampToInclusive'] !== 'undefined') {
      queryParameters['createdTimestampToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdTimestampToInclusive'],
          ''
        );
    }

    if (typeof parameters['documentTypes'] !== 'undefined') {
      queryParameters['documentTypes'] =
        Backend.convertParameterCollectionFormat(
          parameters['documentTypes'],
          ''
        );
    }

    if (typeof parameters['folderId'] !== 'undefined') {
      queryParameters['folderId'] = Backend.convertParameterCollectionFormat(
        parameters['folderId'],
        ''
      );
    }

    if (typeof parameters['ownerIds'] !== 'undefined') {
      queryParameters['ownerIds'] = Backend.convertParameterCollectionFormat(
        parameters['ownerIds'],
        ''
      );
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['sortFields'] !== 'undefined') {
      queryParameters['sortFields'] = Backend.convertParameterCollectionFormat(
        parameters['sortFields'],
        ''
      );
    }

    if (typeof parameters['sortOrders'] !== 'undefined') {
      queryParameters['sortOrders'] = Backend.convertParameterCollectionFormat(
        parameters['sortOrders'],
        ''
      );
    }

    if (typeof parameters['tagIds'] !== 'undefined') {
      queryParameters['tagIds'] = Backend.convertParameterCollectionFormat(
        parameters['tagIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#areAllDocumentsMovableToAnotherCasus
   * @param {string} casusId -
   * @param {array} documentIds -
   */
  public async areAllDocumentsMovableToAnotherCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentIds: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: AreAllDocumentsMovableToAnotherCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, AreAllDocumentsMovableToAnotherCasusResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, AreAllDocumentsMovableToAnotherCasusResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentIds'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentIds'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.areAllDocumentsMovableToAnotherCasusURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      AreAllDocumentsMovableToAnotherCasusResponse
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          AreAllDocumentsMovableToAnotherCasusResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as AreAllDocumentsMovableToAnotherCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        AreAllDocumentsMovableToAnotherCasusResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public areAllDocumentsMovableToAnotherCasusURL(
    parameters: {
      casusId: string;
      documentIds: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/are-all-documents-movable-to-another-casus/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['documentIds'] !== 'undefined') {
      queryParameters['documentIds'] = Backend.convertParameterCollectionFormat(
        parameters['documentIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createEmails
   * @param {} body -
   * @param {string} casusId -
   */
  public async createEmails({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateEmailsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, NotConvertibleToEmailCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, NotConvertibleToEmailCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createEmailsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, NotConvertibleToEmailCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, NotConvertibleToEmailCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, NotConvertibleToEmailCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createEmailsURL(
    parameters: {
      body: CreateEmailsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/create-emails/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createMultiplePdfs
   * @param {} body -
   * @param {string} casusId -
   */
  public async createMultiplePdfs({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateMultiplePdfsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, NotConvertibleToPdfCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, NotConvertibleToPdfCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createMultiplePdfsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, NotConvertibleToPdfCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, NotConvertibleToPdfCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, NotConvertibleToPdfCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createMultiplePdfsURL(
    parameters: {
      body: CreateMultiplePdfsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/create-pdfs/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#digitallySignDocument
   * @param {} body -
   * @param {string} casusId -
   */
  public async digitallySignDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DigitalSigningParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentDigitalSigningCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentDigitalSigningCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.digitallySignDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentDigitalSigningCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentDigitalSigningCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentDigitalSigningCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public digitallySignDocumentURL(
    parameters: {
      body: DigitalSigningParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/digital-signatures/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#extractZipFiles
   * @param {} body -
   * @param {string} casusId -
   */
  public async extractZipFiles({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ZipFilesExtractionParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.extractZipFilesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public extractZipFilesURL(
    parameters: {
      body: ZipFilesExtractionParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/extract-zip-files/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#moveDocuments
   * @param {} body -
   * @param {string} casusId -
   */
  public async moveDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: MoveDocumentsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Unit;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, Unit>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, Unit>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.moveDocumentsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, Unit>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, Unit>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Unit,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, Unit>
        | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public moveDocumentsURL(
    parameters: {
      body: MoveDocumentsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/move-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#sendByMail
   * @param {} body -
   * @param {string} casusId -
   */
  public async sendByMail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SendByMailParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SharedDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, SharedDocumentsResponse>
          | ResponseWithBody<400, NotConvertibleToPdfCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, SharedDocumentsResponse>
      | ResponseWithBody<400, NotConvertibleToPdfCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.sendByMailURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, SharedDocumentsResponse>
      | ResponseWithBody<400, NotConvertibleToPdfCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, SharedDocumentsResponse>
          | ResponseWithBody<400, NotConvertibleToPdfCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SharedDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, SharedDocumentsResponse>
        | ResponseWithBody<400, NotConvertibleToPdfCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public sendByMailURL(
    parameters: {
      body: SendByMailParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/send-by-mail/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#shareWithUser
   * @param {} body -
   * @param {string} casusId -
   */
  public async shareWithUser({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ShareWithUserParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SharedDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SharedDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, SharedDocumentsResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.shareWithUserURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SharedDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SharedDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SharedDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SharedDocumentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public shareWithUserURL(
    parameters: {
      body: ShareWithUserParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/share-with-user/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#signDocuments
   * @param {} body -
   * @param {string} casusId -
   */
  public async signDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SignDocumentsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentSigningCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentSigningCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.signDocumentsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentSigningCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentSigningCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentSigningCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public signDocumentsURL(
    parameters: {
      body: SignDocumentsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/sign-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setTags
   * @param {} body -
   * @param {string} casusId -
   */
  public async setTags({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: Array<TagAssignmentParams>;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setTagsURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public setTagsURL(
    parameters: {
      body: Array<TagAssignmentParams>;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/tags/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#zipDocuments
   * @param {} body -
   * @param {string} casusId -
   */
  public async zipDocuments({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: ZipDocumentsParams;
      casusId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.zipDocumentsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public zipDocumentsURL(
    parameters: {
      body: ZipDocumentsParams;
      casusId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/zip-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#renameDocument
   * @param {} body -
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async renameDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DocumentRenameParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: RenameDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, RenameDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, RenameDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.renameDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, RenameDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, RenameDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as RenameDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, RenameDocumentResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public renameDocumentURL(
    parameters: {
      body: DocumentRenameParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/documents/{documentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#copyDocument
   * @param {} body -
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async copyDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DocumentCopyParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CopyDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CopyDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CopyDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.copyDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CopyDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CopyDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CopyDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CopyDocumentResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public copyDocumentURL(
    parameters: {
      body: DocumentCopyParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/copy/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createFromTemplate
   * @param {} body -
   * @param {string} documentId -
   */
  public async createFromTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateFromTemplateParams;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateFromTemplateResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateFromTemplateResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateFromTemplateResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createFromTemplateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateFromTemplateResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateFromTemplateResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateFromTemplateResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateFromTemplateResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createFromTemplateURL(
    parameters: {
      body: CreateFromTemplateParams;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/create-from-template/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#setDescription
   * @param {} body -
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async setDescription({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: SetDescriptionParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UpdateDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UpdateDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UpdateDocumentResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.setDescriptionURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UpdateDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UpdateDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UpdateDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UpdateDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public setDescriptionURL(
    parameters: {
      body: SetDescriptionParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/description/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadDocumentVersion
   * @param {string} casusId -
   * @param {string} documentId -
   * @param {string} documentVersionId -
   */
  public async downloadDocumentVersion({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
      documentVersionId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadDocumentVersion_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadDocumentVersion_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadDocumentVersion_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      if (typeof parameters['documentVersionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentVersionId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadDocumentVersionURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadDocumentVersion_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadDocumentVersion_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadDocumentVersion_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadDocumentVersion_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadDocumentVersionURL(
    parameters: {
      casusId: string;
      documentId: string;
      documentVersionId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/document-versions/{documentVersionId}/download/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentVersionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentVersionId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadDocumentVersionPdf
   * @param {string} casusId -
   * @param {string} documentId -
   * @param {string} documentVersionId -
   * @param {array} highlightTexts -
   */
  public async downloadDocumentVersionPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
      documentVersionId: string;
      highlightTexts?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadDocumentVersionPdf_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadDocumentVersionPdf_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadDocumentVersionPdf_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'application/pdf';

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      if (typeof parameters['documentVersionId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentVersionId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadDocumentVersionPdfURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadDocumentVersionPdf_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadDocumentVersionPdf_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadDocumentVersionPdf_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadDocumentVersionPdf_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadDocumentVersionPdfURL(
    parameters: {
      casusId: string;
      documentId: string;
      documentVersionId: string;
      highlightTexts?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/document-versions/{documentVersionId}/pdf/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentVersionId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentVersionId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['highlightTexts'] !== 'undefined') {
      queryParameters['highlightTexts'] =
        Backend.convertParameterCollectionFormat(
          parameters['highlightTexts'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#download
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async download({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_download_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_download_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_download_200>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.downloadURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_download_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_download_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_download_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_download_200>,
      header: result.header,
      hasError: true,
    };
  }

  public downloadURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/download/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#duplicateDocument
   * @param {} body -
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async duplicateDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: DocumentDuplicateParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: DuplicateDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, DuplicateDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, DuplicateDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.duplicateDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, DuplicateDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, DuplicateDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as DuplicateDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, DuplicateDocumentResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public duplicateDocumentURL(
    parameters: {
      body: DocumentDuplicateParams;
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/duplicate/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getInactiveDocumentVersions
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async getInactiveDocumentVersions({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetInactiveDocumentVersionsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, GetInactiveDocumentVersionsResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetInactiveDocumentVersionsResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getInactiveDocumentVersionsURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetInactiveDocumentVersionsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetInactiveDocumentVersionsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetInactiveDocumentVersionsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetInactiveDocumentVersionsResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getInactiveDocumentVersionsURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/inactive-versions/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#lockDocument
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async lockDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.lockDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public lockDocumentURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/lock/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#open
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async open({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_open_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, Response_open_200>
          | ResponseWithBody<400, DocumentLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, Response_open_200>
      | ResponseWithBody<400, DocumentLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.openURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, Response_open_200>
      | ResponseWithBody<400, DocumentLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, Response_open_200>
          | ResponseWithBody<400, DocumentLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_open_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, Response_open_200>
        | ResponseWithBody<400, DocumentLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public openURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/open/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadPdf
   * @param {string} casusId -
   * @param {string} documentId -
   * @param {array} highlightTexts -
   */
  public async downloadPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
      highlightTexts?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadPdf_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_downloadPdf_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, Response_downloadPdf_200>> =
      new Promise((resolve, reject) => {
        headers['accept'] = 'application/pdf';

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.downloadPdfURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadPdf_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadPdf_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadPdf_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, Response_downloadPdf_200>,
      header: result.header,
      hasError: true,
    };
  }

  public downloadPdfURL(
    parameters: {
      casusId: string;
      documentId: string;
      highlightTexts?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/pdf/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['highlightTexts'] !== 'undefined') {
      queryParameters['highlightTexts'] =
        Backend.convertParameterCollectionFormat(
          parameters['highlightTexts'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadThumbnail
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async downloadThumbnail({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadThumbnail_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_downloadThumbnail_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadThumbnail_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'image/png';

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadThumbnailURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadThumbnail_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadThumbnail_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadThumbnail_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadThumbnail_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadThumbnailURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/thumbnail/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#unlockDocument
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async unlockDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.unlockDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public unlockDocumentURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/unlock/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#uploadNewVersion
   * @param {file} file -
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async uploadNewVersion({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UpdateDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UpdateDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UpdateDocumentResponse>> =
      new Promise((resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.uploadNewVersionURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UpdateDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UpdateDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UpdateDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UpdateDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public uploadNewVersionURL(
    parameters: {
      file: {};
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/documents/{documentId}/upload-new-version/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getFolderHierarchy
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async getFolderHierarchy({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      folderId?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetFolderHierarchyResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetFolderHierarchyResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetFolderHierarchyResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getFolderHierarchyURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetFolderHierarchyResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetFolderHierarchyResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetFolderHierarchyResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetFolderHierarchyResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getFolderHierarchyURL(
    parameters: {
      casusId: string;
      folderId?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/folders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['folderId'] !== 'undefined') {
      queryParameters['folderId'] = Backend.convertParameterCollectionFormat(
        parameters['folderId'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getFolderHierarchyForDocument
   * @param {string} casusId -
   * @param {string} selectedDocumentId -
   */
  public async getFolderHierarchyForDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      selectedDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetFolderHierarchyResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetFolderHierarchyResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetFolderHierarchyResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['selectedDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: selectedDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getFolderHierarchyForDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetFolderHierarchyResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetFolderHierarchyResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetFolderHierarchyResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetFolderHierarchyResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getFolderHierarchyForDocumentURL(
    parameters: {
      casusId: string;
      selectedDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/folder-by-document/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['selectedDocumentId'] !== 'undefined') {
      queryParameters['selectedDocumentId'] =
        Backend.convertParameterCollectionFormat(
          parameters['selectedDocumentId'],
          ''
        );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteFolder
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async deleteFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'DELETE',
        this.deleteFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteFolderURL(
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/folders/{folderId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#renameFolder
   * @param {} body -
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async renameFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FolderRenameParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: RenameFolderResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, RenameFolderResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, RenameFolderResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.renameFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, RenameFolderResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, RenameFolderResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as RenameFolderResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, RenameFolderResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public renameFolderURL(
    parameters: {
      body: FolderRenameParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/folders/{folderId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getFolderContentTypes
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async getFolderContentTypes({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetFolderContentTypesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetFolderContentTypesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetFolderContentTypesResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getFolderContentTypesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetFolderContentTypesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetFolderContentTypesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetFolderContentTypesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetFolderContentTypesResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getFolderContentTypesURL(
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/content-types/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkDocumentNameAvailability
   * @param {string} casusId -
   * @param {string} excludedDocumentId -
   * @param {string} folderId -
   * @param {string} itemName -
   */
  public async checkDocumentNameAvailability({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      excludedDocumentId?: string;
      folderId: string;
      itemName: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CheckNameAvailabilityResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CheckNameAvailabilityResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CheckNameAvailabilityResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      if (typeof parameters['itemName'] === 'undefined') {
        reject(new Error('Missing required  parameter: itemName'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.checkDocumentNameAvailabilityURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CheckNameAvailabilityResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CheckNameAvailabilityResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CheckNameAvailabilityResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CheckNameAvailabilityResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public checkDocumentNameAvailabilityURL(
    parameters: {
      casusId: string;
      excludedDocumentId?: string;
      folderId: string;
      itemName: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/document-name-availability/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['excludedDocumentId'] !== 'undefined') {
      queryParameters['excludedDocumentId'] =
        Backend.convertParameterCollectionFormat(
          parameters['excludedDocumentId'],
          ''
        );
    }

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemName'] !== 'undefined') {
      queryParameters['itemName'] = Backend.convertParameterCollectionFormat(
        parameters['itemName'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createDocument
   * @param {file} file -
   * @param {string} casusId -
   * @param {string} createdAt -
   * @param {string} folderId -
   * @param {string} modifiedAt -
   */
  public async createDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      casusId: string;
      createdAt?: string;
      folderId: string;
      modifiedAt?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, CreateDocumentResponse>> =
      new Promise((resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['folderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: folderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, CreateDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createDocumentURL(
    parameters: {
      file: {};
      casusId: string;
      createdAt?: string;
      folderId: string;
      modifiedAt?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['createdAt'] !== 'undefined') {
      queryParameters['createdAt'] = Backend.convertParameterCollectionFormat(
        parameters['createdAt'],
        ''
      );
    }

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['modifiedAt'] !== 'undefined') {
      queryParameters['modifiedAt'] = Backend.convertParameterCollectionFormat(
        parameters['modifiedAt'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getNumberOfDocumentsInFolder
   * @param {string} casusId -
   * @param {string} createdTimestampFromInclusive -
   * @param {string} createdTimestampToInclusive -
   * @param {array} documentTypes -
   * @param {string} folderId -
   * @param {array} ownerIds -
   * @param {string} query -
   * @param {array} tagIds -
   */
  public async getNumberOfDocumentsInFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      createdTimestampFromInclusive?: string;
      createdTimestampToInclusive?: string;
      documentTypes: Array<
        | 'EMAIL_DOCUMENT'
        | 'EXCEL_DOCUMENT'
        | 'POWERPOINT_DOCUMENT'
        | 'PDF_DOCUMENT'
        | 'WORD_DOCUMENT'
        | 'IMAGE_DOCUMENT'
        | 'TEXT_DOCUMENT'
        | 'RTF_DOCUMENT'
        | 'ZIP_DOCUMENT'
        | 'UNKNOWN_DOCUMENT'
      >;
      folderId: string;
      ownerIds: Array<string>;
      query?: string;
      tagIds: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetNumberOfDocumentsInFolderResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, GetNumberOfDocumentsInFolderResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetNumberOfDocumentsInFolderResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentTypes'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentTypes'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      if (typeof parameters['ownerIds'] === 'undefined') {
        reject(new Error('Missing required  parameter: ownerIds'));
        return;
      }

      if (typeof parameters['tagIds'] === 'undefined') {
        reject(new Error('Missing required  parameter: tagIds'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getNumberOfDocumentsInFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetNumberOfDocumentsInFolderResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetNumberOfDocumentsInFolderResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetNumberOfDocumentsInFolderResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetNumberOfDocumentsInFolderResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getNumberOfDocumentsInFolderURL(
    parameters: {
      casusId: string;
      createdTimestampFromInclusive?: string;
      createdTimestampToInclusive?: string;
      documentTypes: Array<
        | 'EMAIL_DOCUMENT'
        | 'EXCEL_DOCUMENT'
        | 'POWERPOINT_DOCUMENT'
        | 'PDF_DOCUMENT'
        | 'WORD_DOCUMENT'
        | 'IMAGE_DOCUMENT'
        | 'TEXT_DOCUMENT'
        | 'RTF_DOCUMENT'
        | 'ZIP_DOCUMENT'
        | 'UNKNOWN_DOCUMENT'
      >;
      folderId: string;
      ownerIds: Array<string>;
      query?: string;
      tagIds: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/documents/count/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['createdTimestampFromInclusive'] !== 'undefined') {
      queryParameters['createdTimestampFromInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdTimestampFromInclusive'],
          ''
        );
    }

    if (typeof parameters['createdTimestampToInclusive'] !== 'undefined') {
      queryParameters['createdTimestampToInclusive'] =
        Backend.convertParameterCollectionFormat(
          parameters['createdTimestampToInclusive'],
          ''
        );
    }

    if (typeof parameters['documentTypes'] !== 'undefined') {
      queryParameters['documentTypes'] =
        Backend.convertParameterCollectionFormat(
          parameters['documentTypes'],
          ''
        );
    }

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['ownerIds'] !== 'undefined') {
      queryParameters['ownerIds'] = Backend.convertParameterCollectionFormat(
        parameters['ownerIds'],
        ''
      );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (typeof parameters['tagIds'] !== 'undefined') {
      queryParameters['tagIds'] = Backend.convertParameterCollectionFormat(
        parameters['tagIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createDocumentFromTemporaryDocument
   * @param {} body -
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async createDocumentFromTemporaryDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateDocumentFromTemporaryDocumentParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreateDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreateDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createDocumentFromTemporaryDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreateDocumentResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreateDocumentResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreateDocumentResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createDocumentFromTemporaryDocumentURL(
    parameters: {
      body: CreateDocumentFromTemporaryDocumentParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/documents/from-temp-document/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#importDocument
   * @param {file} file -
   * @param {string} importEmailFileAsEmail -
   * @param {string} casusId -
   * @param {string} createdAt -
   * @param {string} folderId -
   * @param {string} modifiedAt -
   */
  public async importDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      importEmailFileAsEmail: string;
      casusId: string;
      createdAt?: string;
      folderId: string;
      modifiedAt?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        if (typeof parameters['importEmailFileAsEmail'] !== 'undefined') {
          form['importEmailFileAsEmail'] = parameters['importEmailFileAsEmail'];
        }

        if (typeof parameters['importEmailFileAsEmail'] === 'undefined') {
          reject(
            new Error('Missing required  parameter: importEmailFileAsEmail')
          );
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['folderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: folderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.importDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public importDocumentURL(
    parameters: {
      file: {};
      importEmailFileAsEmail: string;
      casusId: string;
      createdAt?: string;
      folderId: string;
      modifiedAt?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/documents/import/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['createdAt'] !== 'undefined') {
      queryParameters['createdAt'] = Backend.convertParameterCollectionFormat(
        parameters['createdAt'],
        ''
      );
    }

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['modifiedAt'] !== 'undefined') {
      queryParameters['modifiedAt'] = Backend.convertParameterCollectionFormat(
        parameters['modifiedAt'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDocument
   * @param {string} casusId -
   * @param {string} documentId -
   * @param {string} folderId -
   */
  public async getDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetDocumentResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        if (typeof parameters['folderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: folderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getDocumentURL(
    parameters: {
      casusId: string;
      documentId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/documents/{documentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#checkFolderNameAvailability
   * @param {string} casusId -
   * @param {string} excludedFolderId -
   * @param {string} folderId -
   * @param {string} itemName -
   */
  public async checkFolderNameAvailability({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      excludedFolderId?: string;
      folderId: string;
      itemName: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CheckNameAvailabilityResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CheckNameAvailabilityResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CheckNameAvailabilityResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      if (typeof parameters['itemName'] === 'undefined') {
        reject(new Error('Missing required  parameter: itemName'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.checkFolderNameAvailabilityURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CheckNameAvailabilityResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CheckNameAvailabilityResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CheckNameAvailabilityResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CheckNameAvailabilityResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public checkFolderNameAvailabilityURL(
    parameters: {
      casusId: string;
      excludedFolderId?: string;
      folderId: string;
      itemName: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/folder-name-availability/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['excludedFolderId'] !== 'undefined') {
      queryParameters['excludedFolderId'] =
        Backend.convertParameterCollectionFormat(
          parameters['excludedFolderId'],
          ''
        );
    }

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['itemName'] !== 'undefined') {
      queryParameters['itemName'] = Backend.convertParameterCollectionFormat(
        parameters['itemName'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getFolders
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async getFolders({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetFoldersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetFoldersResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetFoldersResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['folderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: folderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getFoldersURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetFoldersResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetFoldersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetFoldersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetFoldersResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getFoldersURL(
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/folders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createFolder
   * @param {} body -
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async createFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FolderCreateParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateFolderResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreateFolderResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreateFolderResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreateFolderResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreateFolderResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateFolderResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreateFolderResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public createFolderURL(
    parameters: {
      body: FolderCreateParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/folders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#importFolder
   * @param {} body -
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async importFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: FolderCreateParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateFolderResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, CreateFolderResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, CreateFolderResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.importFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, CreateFolderResponse>
      | ResponseWithBody<400, NonUniqueItemNameCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, CreateFolderResponse>
          | ResponseWithBody<400, NonUniqueItemNameCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateFolderResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, CreateFolderResponse>
        | ResponseWithBody<400, NonUniqueItemNameCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public importFolderURL(
    parameters: {
      body: FolderCreateParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/folders/import/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#isFolderMovableToAnotherCasus
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async isFolderMovableToAnotherCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: IsFolderMovableToAnotherCasusResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, IsFolderMovableToAnotherCasusResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, IsFolderMovableToAnotherCasusResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.isFolderMovableToAnotherCasusURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, IsFolderMovableToAnotherCasusResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          IsFolderMovableToAnotherCasusResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as IsFolderMovableToAnotherCasusResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        IsFolderMovableToAnotherCasusResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public isFolderMovableToAnotherCasusURL(
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/is-movable-to-another-casus/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#moveFolder
   * @param {} body -
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async moveFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: MoveFolderParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: MoveFolderResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, MoveFolderResponse>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, MoveFolderResponse>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.moveFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, MoveFolderResponse>
      | ResponseWithBody<400, DocumentInUseOrLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, MoveFolderResponse>
          | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as MoveFolderResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, MoveFolderResponse>
        | ResponseWithBody<400, DocumentInUseOrLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public moveFolderURL(
    parameters: {
      body: MoveFolderParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/folders/{folderId}/move/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#documentUploadFinished
   * @param {} body -
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async documentUploadFinished({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: UploadedDocumentsParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['casusId'] === 'undefined') {
          reject(new Error('Missing required  parameter: casusId'));
          return;
        }

        if (typeof parameters['folderId'] === 'undefined') {
          reject(new Error('Missing required  parameter: folderId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.documentUploadFinishedURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public documentUploadFinishedURL(
    parameters: {
      body: UploadedDocumentsParams;
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/folders/{folderId}/upload-finished/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#zipFolder
   * @param {string} casusId -
   * @param {string} folderId -
   */
  public async zipFolder({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['folderId'] === 'undefined') {
        reject(new Error('Missing required  parameter: folderId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.zipFolderURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public zipFolderURL(
    parameters: {
      casusId: string;
      folderId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/casus/{casusId}/folders/{folderId}/zip/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{folderId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['folderId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getLockedDocumentById
   * @param {string} casusId -
   * @param {string} documentId -
   */
  public async getLockedDocumentById({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetLockedDocumentByIdResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetLockedDocumentByIdResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetLockedDocumentByIdResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['casusId'] === 'undefined') {
        reject(new Error('Missing required  parameter: casusId'));
        return;
      }

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getLockedDocumentByIdURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetLockedDocumentByIdResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetLockedDocumentByIdResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetLockedDocumentByIdResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetLockedDocumentByIdResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getLockedDocumentByIdURL(
    parameters: {
      casusId: string;
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/casus/{casusId}/locked-documents/{documentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{casusId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['casusId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#removeDocumentLocks
   * @param {integer} daysInPast -
   * @param {array} userIds -
   */
  public async removeDocumentLocks({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      daysInPast?: number;
      userIds?: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.removeDocumentLocksURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public removeDocumentLocksURL(
    parameters: {
      daysInPast?: number;
      userIds?: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/document-locks/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['daysInPast'] !== 'undefined') {
      queryParameters['daysInPast'] = Backend.convertParameterCollectionFormat(
        parameters['daysInPast'],
        ''
      );
    }

    if (typeof parameters['userIds'] !== 'undefined') {
      queryParameters['userIds'] = Backend.convertParameterCollectionFormat(
        parameters['userIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getAllDocumentLocks
   */
  public async getAllDocumentLocks({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getAllDocumentLocks_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getAllDocumentLocks_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getAllDocumentLocks_200>
    > = new Promise((resolve, reject) => {
      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getAllDocumentLocksURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getAllDocumentLocks_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getAllDocumentLocks_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getAllDocumentLocks_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getAllDocumentLocks_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getAllDocumentLocksURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/document-locks/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#removeDocumentLock
   * @param {string} documentId -
   */
  public async removeDocumentLock({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.removeDocumentLockURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public removeDocumentLockURL(
    parameters: {
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/document-locks/{documentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDocumentTemplates
   */
  public async getDocumentTemplates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetTemplatesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetTemplatesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetTemplatesResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getDocumentTemplatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetTemplatesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetTemplatesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetTemplatesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetTemplatesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getDocumentTemplatesURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/document-templates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#lockDocumentWithoutCasus
   * @param {string} documentId -
   */
  public async lockDocumentWithoutCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | (
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>
        )
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'PUT',
        this.lockDocumentWithoutCasusURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result:
      | ResponseWithBody<200, void>
      | ResponseWithBody<400, DocumentLockedCondition>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as
          | ResponseWithBody<200, void>
          | ResponseWithBody<400, DocumentLockedCondition>,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as
        | ResponseWithBody<200, void>
        | ResponseWithBody<400, DocumentLockedCondition>,
      header: result.header,
      hasError: true,
    };
  }

  public lockDocumentWithoutCasusURL(
    parameters: {
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/documents/{documentId}/lock/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDocumentPreview
   * @param {string} documentId -
   * @param {string} type -
   */
  public async getDocumentPreview({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      documentId: string;
      type?:
        | 'JUSTITIA_SUBMISSION_DOCUMENT'
        | 'JUSTITIA_SUBMISSION_RECEIPT_DOCUMENT'
        | 'JUSTITIA_RECEIVAL_RECEIPT_DOCUMENT';
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getDocumentPreview_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getDocumentPreview_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getDocumentPreview_200>
    > = new Promise((resolve, reject) => {
      headers['accept'] = 'application/pdf';

      if (typeof parameters['documentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getDocumentPreviewURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getDocumentPreview_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getDocumentPreview_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getDocumentPreview_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getDocumentPreview_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getDocumentPreviewURL(
    parameters: {
      documentId: string;
      type?:
        | 'JUSTITIA_SUBMISSION_DOCUMENT'
        | 'JUSTITIA_SUBMISSION_RECEIPT_DOCUMENT'
        | 'JUSTITIA_RECEIVAL_RECEIPT_DOCUMENT';
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/documents/{documentId}/pdf/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );
    if (typeof parameters['type'] !== 'undefined') {
      queryParameters['type'] = Backend.convertParameterCollectionFormat(
        parameters['type'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#unlockDocumentWithoutCasus
   * @param {string} documentId -
   */
  public async unlockDocumentWithoutCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.unlockDocumentWithoutCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public unlockDocumentWithoutCasusURL(
    parameters: {
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/documents/{documentId}/unlock/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#uploadNewVersionWithoutCasus
   * @param {file} file -
   * @param {string} documentId -
   */
  public async uploadNewVersionWithoutCasus({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      documentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: UpdateDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, UpdateDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, UpdateDocumentResponse>> =
      new Promise((resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        if (typeof parameters['documentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: documentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.uploadNewVersionWithoutCasusURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, UpdateDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          UpdateDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as UpdateDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, UpdateDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public uploadNewVersionWithoutCasusURL(
    parameters: {
      file: {};
      documentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/documents/{documentId}/upload-new-version/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{documentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['documentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#searchEmailAddresses
   * @param {string} paginationCursor -
   * @param {string} paginationDirection -
   * @param {array} paginationKeyComponents -
   * @param {string} paginationMode -
   * @param {array} paginationSortFieldValues -
   * @param {string} query -
   */
  public async searchEmailAddresses({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: SearchEmailAddressesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, SearchEmailAddressesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, SearchEmailAddressesResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['paginationDirection'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationDirection'));
        return;
      }

      if (typeof parameters['paginationMode'] === 'undefined') {
        reject(new Error('Missing required  parameter: paginationMode'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.searchEmailAddressesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, SearchEmailAddressesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          SearchEmailAddressesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as SearchEmailAddressesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, SearchEmailAddressesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public searchEmailAddressesURL(
    parameters: {
      paginationCursor?: string;
      paginationDirection: 'FORWARD' | 'BACKWARD';
      paginationKeyComponents?: Array<string>;
      paginationMode:
        | 'BROWSE'
        | 'REFRESH'
        | 'NAVIGATE_TO_ITEM'
        | 'JUMP_TO_POSITION';
      paginationSortFieldValues?: Array<string>;
      query?: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/email-addresses/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['paginationCursor'] !== 'undefined') {
      queryParameters['paginationCursor'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationCursor'],
          ''
        );
    }

    if (typeof parameters['paginationDirection'] !== 'undefined') {
      queryParameters['paginationDirection'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationDirection'],
          ''
        );
    }

    if (typeof parameters['paginationKeyComponents'] !== 'undefined') {
      queryParameters['paginationKeyComponents'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationKeyComponents'],
          ''
        );
    }

    if (typeof parameters['paginationMode'] !== 'undefined') {
      queryParameters['paginationMode'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationMode'],
          ''
        );
    }

    if (typeof parameters['paginationSortFieldValues'] !== 'undefined') {
      queryParameters['paginationSortFieldValues'] =
        Backend.convertParameterCollectionFormat(
          parameters['paginationSortFieldValues'],
          ''
        );
    }

    if (typeof parameters['query'] !== 'undefined') {
      queryParameters['query'] = Backend.convertParameterCollectionFormat(
        parameters['query'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getEmailTemplates
   */
  public async getEmailTemplates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetTemplatesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetTemplatesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetTemplatesResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getEmailTemplatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetTemplatesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetTemplatesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetTemplatesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetTemplatesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getEmailTemplatesURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/email-templates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDocumentsLockState
   * @param {array} documentIds -
   */
  public async getDocumentsLockState({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      documentIds: Array<string>;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getDocumentsLockState_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, Response_getDocumentsLockState_200> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getDocumentsLockState_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['documentIds'] === 'undefined') {
        reject(new Error('Missing required  parameter: documentIds'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getDocumentsLockStateURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_getDocumentsLockState_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getDocumentsLockState_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getDocumentsLockState_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getDocumentsLockState_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getDocumentsLockStateURL(
    parameters: {
      documentIds: Array<string>;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/lock-state/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['documentIds'] !== 'undefined') {
      queryParameters['documentIds'] = Backend.convertParameterCollectionFormat(
        parameters['documentIds'],
        ''
      );
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getLockedDocumentsOfLoggedInUser
   */
  public async getLockedDocumentsOfLoggedInUser({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetLockedDocumentsResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetLockedDocumentsResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetLockedDocumentsResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getLockedDocumentsOfLoggedInUserURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetLockedDocumentsResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetLockedDocumentsResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetLockedDocumentsResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetLockedDocumentsResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getLockedDocumentsOfLoggedInUserURL(
    parameters: CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/locked-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getReportTemplates
   */
  public async getReportTemplates({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetTemplatesResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, GetTemplatesResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, GetTemplatesResponse>> =
      new Promise((resolve, reject) => {
        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'GET',
          this.getReportTemplatesURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, GetTemplatesResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetTemplatesResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetTemplatesResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, GetTemplatesResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public getReportTemplatesURL(parameters: CommonRequestOptions): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/report-templates/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getDependentPlaceholderValues
   * @param {string} casusId -
   * @param {string} locale -
   * @param {string} pageKey -
   * @param {string} parentEntityId -
   * @param {string} parentPlaceholderKey -
   * @param {string} placeholderKey -
   * @param {string} templateFileId -
   * @param {string} templateId -
   */
  public async getDependentPlaceholderValues({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId?: string;
      locale: string;
      pageKey: string;
      parentEntityId: string;
      parentPlaceholderKey: string;
      placeholderKey: string;
      templateFileId: string;
      templateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_getDependentPlaceholderValues_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_getDependentPlaceholderValues_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_getDependentPlaceholderValues_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['locale'] === 'undefined') {
        reject(new Error('Missing required  parameter: locale'));
        return;
      }

      if (typeof parameters['pageKey'] === 'undefined') {
        reject(new Error('Missing required  parameter: pageKey'));
        return;
      }

      if (typeof parameters['parentEntityId'] === 'undefined') {
        reject(new Error('Missing required  parameter: parentEntityId'));
        return;
      }

      if (typeof parameters['parentPlaceholderKey'] === 'undefined') {
        reject(new Error('Missing required  parameter: parentPlaceholderKey'));
        return;
      }

      if (typeof parameters['placeholderKey'] === 'undefined') {
        reject(new Error('Missing required  parameter: placeholderKey'));
        return;
      }

      if (typeof parameters['templateFileId'] === 'undefined') {
        reject(new Error('Missing required  parameter: templateFileId'));
        return;
      }

      if (typeof parameters['templateId'] === 'undefined') {
        reject(new Error('Missing required  parameter: templateId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getDependentPlaceholderValuesURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      Response_getDependentPlaceholderValues_200
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_getDependentPlaceholderValues_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_getDependentPlaceholderValues_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_getDependentPlaceholderValues_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getDependentPlaceholderValuesURL(
    parameters: {
      casusId?: string;
      locale: string;
      pageKey: string;
      parentEntityId: string;
      parentPlaceholderKey: string;
      placeholderKey: string;
      templateFileId: string;
      templateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/templates/{templateId}/template-files/{templateFileId}/dependent-placeholder-values/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['locale'] !== 'undefined') {
      queryParameters['locale'] = Backend.convertParameterCollectionFormat(
        parameters['locale'],
        ''
      );
    }

    if (typeof parameters['pageKey'] !== 'undefined') {
      queryParameters['pageKey'] = Backend.convertParameterCollectionFormat(
        parameters['pageKey'],
        ''
      );
    }

    if (typeof parameters['parentEntityId'] !== 'undefined') {
      queryParameters['parentEntityId'] =
        Backend.convertParameterCollectionFormat(
          parameters['parentEntityId'],
          ''
        );
    }

    if (typeof parameters['parentPlaceholderKey'] !== 'undefined') {
      queryParameters['parentPlaceholderKey'] =
        Backend.convertParameterCollectionFormat(
          parameters['parentPlaceholderKey'],
          ''
        );
    }

    if (typeof parameters['placeholderKey'] !== 'undefined') {
      queryParameters['placeholderKey'] =
        Backend.convertParameterCollectionFormat(
          parameters['placeholderKey'],
          ''
        );
    }

    path = path.replace(
      '{templateFileId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['templateFileId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{templateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['templateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#getOverridableTemplatePlaceholders
   * @param {string} casusId -
   * @param {string} locale -
   * @param {string} templateFileId -
   * @param {string} templateId -
   */
  public async getOverridableTemplatePlaceholders({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      casusId?: string;
      locale: string;
      templateFileId: string;
      templateId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: GetOverridableTemplatePlaceholdersResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, GetOverridableTemplatePlaceholdersResponse>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, GetOverridableTemplatePlaceholdersResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['locale'] === 'undefined') {
        reject(new Error('Missing required  parameter: locale'));
        return;
      }

      if (typeof parameters['templateFileId'] === 'undefined') {
        reject(new Error('Missing required  parameter: templateFileId'));
        return;
      }

      if (typeof parameters['templateId'] === 'undefined') {
        reject(new Error('Missing required  parameter: templateId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.getOverridableTemplatePlaceholdersURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      GetOverridableTemplatePlaceholdersResponse
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          GetOverridableTemplatePlaceholdersResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as GetOverridableTemplatePlaceholdersResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        GetOverridableTemplatePlaceholdersResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public getOverridableTemplatePlaceholdersURL(
    parameters: {
      casusId?: string;
      locale: string;
      templateFileId: string;
      templateId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/templates/{templateId}/template-files/{templateFileId}/placeholders/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['casusId'] !== 'undefined') {
      queryParameters['casusId'] = Backend.convertParameterCollectionFormat(
        parameters['casusId'],
        ''
      );
    }

    if (typeof parameters['locale'] !== 'undefined') {
      queryParameters['locale'] = Backend.convertParameterCollectionFormat(
        parameters['locale'],
        ''
      );
    }

    path = path.replace(
      '{templateFileId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['templateFileId'],
          ''
        ).toString()
      )}`
    );

    path = path.replace(
      '{templateId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['templateId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createTemporaryDocument
   * @param {file} file -
   */
  public async createTemporaryDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TemporaryDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TemporaryDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, TemporaryDocumentResponse>> =
      new Promise((resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.createTemporaryDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TemporaryDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TemporaryDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TemporaryDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, TemporaryDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public createTemporaryDocumentURL(
    parameters: {
      file: {};
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/temporary-documents/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#createTemporaryDocumentFromTemplate
   * @param {} body -
   */
  public async createTemporaryDocumentFromTemplate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: CreateTemporaryDocumentFromTemplateParams;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: CreateTemporaryDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, CreateTemporaryDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, CreateTemporaryDocumentResponse>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['body'] !== 'undefined') {
        body = Reflect.get(parameters, 'body');
      }

      if (typeof parameters['body'] === 'undefined') {
        reject(new Error('Missing required  parameter: body'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'POST',
        this.createTemporaryDocumentFromTemplateURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, CreateTemporaryDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          CreateTemporaryDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as CreateTemporaryDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        CreateTemporaryDocumentResponse
      >,
      header: result.header,
      hasError: true,
    };
  }

  public createTemporaryDocumentFromTemplateURL(
    parameters: {
      body: CreateTemporaryDocumentFromTemplateParams;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/temporary-documents-from-template/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#deleteTemporaryDocument
   * @param {string} temporaryDocumentId -
   */
  public async deleteTemporaryDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        if (typeof parameters['temporaryDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: temporaryDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'DELETE',
          this.deleteTemporaryDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public deleteTemporaryDocumentURL(
    parameters: {
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/temporary-documents/{temporaryDocumentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{temporaryDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadTemporaryDocument
   * @param {string} fileName -
   * @param {string} temporaryDocumentId -
   */
  public async downloadTemporaryDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      fileName?: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadTemporaryDocument_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadTemporaryDocument_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadTemporaryDocument_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['temporaryDocumentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: temporaryDocumentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadTemporaryDocumentURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, Response_downloadTemporaryDocument_200>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadTemporaryDocument_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadTemporaryDocument_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadTemporaryDocument_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadTemporaryDocumentURL(
    parameters: {
      fileName?: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/temporary-documents/{temporaryDocumentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['fileName'] !== 'undefined') {
      queryParameters['fileName'] = Backend.convertParameterCollectionFormat(
        parameters['fileName'],
        ''
      );
    }

    path = path.replace(
      '{temporaryDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#replaceTemporaryDocument
   * @param {file} file -
   * @param {string} temporaryDocumentId -
   */
  public async replaceTemporaryDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TemporaryDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TemporaryDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, TemporaryDocumentResponse>> =
      new Promise((resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        if (typeof parameters['temporaryDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: temporaryDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'POST',
          this.replaceTemporaryDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TemporaryDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TemporaryDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TemporaryDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, TemporaryDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public replaceTemporaryDocumentURL(
    parameters: {
      file: {};
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/temporary-documents/{temporaryDocumentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{temporaryDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#renameTemporaryDocument
   * @param {} body -
   * @param {string} temporaryDocumentId -
   */
  public async renameTemporaryDocument({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      body: TemporaryDocumentRenameParams;
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: TemporaryDocumentResponse;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, TemporaryDocumentResponse> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, TemporaryDocumentResponse>> =
      new Promise((resolve, reject) => {
        if (typeof parameters['body'] !== 'undefined') {
          body = Reflect.get(parameters, 'body');
        }

        if (typeof parameters['body'] === 'undefined') {
          reject(new Error('Missing required  parameter: body'));
          return;
        }

        if (typeof parameters['temporaryDocumentId'] === 'undefined') {
          reject(new Error('Missing required  parameter: temporaryDocumentId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.renameTemporaryDocumentURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, TemporaryDocumentResponse>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          TemporaryDocumentResponse
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as TemporaryDocumentResponse,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, TemporaryDocumentResponse>,
      header: result.header,
      hasError: true,
    };
  }

  public renameTemporaryDocumentURL(
    parameters: {
      body: TemporaryDocumentRenameParams;
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/temporary-documents/{temporaryDocumentId}/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{temporaryDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#downloadTemporaryDocumentPdf
   * @param {string} fileName -
   * @param {string} temporaryDocumentId -
   */
  public async downloadTemporaryDocumentPdf({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      fileName?: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: Response_downloadTemporaryDocumentPdf_200;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body:
      | ResponseWithBody<200, Response_downloadTemporaryDocumentPdf_200>
      | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<
      ResponseWithBody<200, Response_downloadTemporaryDocumentPdf_200>
    > = new Promise((resolve, reject) => {
      if (typeof parameters['temporaryDocumentId'] === 'undefined') {
        reject(new Error('Missing required  parameter: temporaryDocumentId'));
        return;
      }

      function progress(event: any): void {
        parameters.$progressCallback?.(event);
      }

      this.request(
        'GET',
        this.downloadTemporaryDocumentPdfURL(parameters),
        body,
        headers,
        form,
        reject,
        resolve,
        progress,
        parameters,
        abortController.signal,
        hasUnauthorizedHandler
      );
    });

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<
      200,
      Response_downloadTemporaryDocumentPdf_200
    >;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          Response_downloadTemporaryDocumentPdf_200
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as Response_downloadTemporaryDocumentPdf_200,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<
        200,
        Response_downloadTemporaryDocumentPdf_200
      >,
      header: result.header,
      hasError: true,
    };
  }

  public downloadTemporaryDocumentPdfURL(
    parameters: {
      fileName?: string;
      temporaryDocumentId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path =
      '/document-management/temporary-documents/{temporaryDocumentId}/pdf/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    if (typeof parameters['fileName'] !== 'undefined') {
      queryParameters['fileName'] = Backend.convertParameterCollectionFormat(
        parameters['fileName'],
        ''
      );
    }

    path = path.replace(
      '{temporaryDocumentId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['temporaryDocumentId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }

  /*
   *
   * @method
   * @name Backend#replaceSigningCertificate
   * @param {file} file -
   * @param {string} userId -
   */
  public async replaceSigningCertificate({
    parameters,
    abortCallbackSetter,
    localHandlers,
    onSuccess,
    afterErrorHandler,
  }: {
    parameters: {
      file: {};
      userId: string;
    } & CommonRequestOptions;
    /**
     * callback function that sets the internal abort function on an object
     */
    abortCallbackSetter?: (abortCallback: () => void) => void;
    /**
     * map of server error keys (as string) and handler functions for the error
     */
    localHandlers?: Record<string, (condition: unknown) => unknown>;
    /**
     * handler which is only called in success case
     */
    onSuccess?: ({
      body,
      header,
      hasError,
    }: {
      body: void;
      header: unknown;
      hasError: boolean;
    }) => void | Promise<void>;
    /**
     * handler which is called regardless of error condition, is called after the condition handler, not called if the request was aborted
     */
    afterErrorHandler?: ({header}?: {header: unknown}) => void | Promise<void>;
  }): Promise<{
    body: ResponseWithBody<200, void> | undefined;
    header?: unknown;
    hasError: boolean;
  }> {
    let body: any;
    let headers: RequestHeaders = {};
    let form: any = {};
    const abortController = new AbortController();

    const _localHandlers = localHandlers ?? {};

    const hasUnauthorizedHandler = Object.entries(_localHandlers).some(
      ([conditionType]) => '401' === conditionType
    );

    const promise: Promise<ResponseWithBody<200, void>> = new Promise(
      (resolve, reject) => {
        headers['content-type'] = 'multipart/form-data';

        if (typeof parameters['file'] !== 'undefined') {
          form['file'] = parameters['file'];
        }

        if (typeof parameters['file'] === 'undefined') {
          reject(new Error('Missing required  parameter: file'));
          return;
        }

        if (typeof parameters['userId'] === 'undefined') {
          reject(new Error('Missing required  parameter: userId'));
          return;
        }

        function progress(event: any): void {
          parameters.$progressCallback?.(event);
        }

        this.request(
          'PUT',
          this.replaceSigningCertificateURL(parameters),
          body,
          headers,
          form,
          reject,
          resolve,
          progress,
          parameters,
          abortController.signal,
          hasUnauthorizedHandler
        );
      }
    );

    if (abortCallbackSetter) {
      abortCallbackSetter(() => {
        abortController.abort();
      });
    }

    let result: ResponseWithBody<200, void>;
    try {
      result = await promise;
    } catch (error) {
      // error with status code
      if (isSuperAgentError(error)) {
        const errorStatus = error.status;
        const handler = (Object.entries(_localHandlers).find(
          ([conditionType]) => String(errorStatus) === conditionType
        ) ||
          Object.entries(getGlobalRequestHandlers()).find(
            ([conditionType]) => String(errorStatus) === conditionType
          ))?.[1];

        if (handler) {
          handler(error);
          afterErrorHandler?.();
          return {body: undefined, hasError: true};
        }
      }

      if (error === 'Aborted') {
        const abortedHandler =
          _localHandlers['Aborted'] ?? getGlobalRequestHandlers()['Aborted'];
        abortedHandler(error);
        return {body: undefined, hasError: true};
      }

      //if no status code was given we have to handle a network error
      const handler =
        _localHandlers['defaultErrorCondition'] ??
        getGlobalRequestHandlers()['defaultErrorCondition'];

      handler(error);
      afterErrorHandler?.();
      return {body: undefined, hasError: true};
    }
    if (result.status >= 200 && result.status < 300) {
      const returnObject = {
        body: {body: result.body, status: result.status} as ResponseWithBody<
          200,
          void
        >,
        header: result.header,
        hasError: false,
      };
      await onSuccess?.({
        body: result.body as void,
        header: result.header,
        hasError: false,
      });
      return returnObject;
    }

    const errorBody = result.body as any;
    const handler =
      (Object.entries(_localHandlers).find(
        ([conditionType, conditionHandler]) =>
          errorBody?.conditionType === conditionType
      ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            errorBody?.conditionType === conditionType
        ) ??
        Object.entries(_localHandlers).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ) ??
        Object.entries(getGlobalRequestHandlers()).find(
          ([conditionType, conditionHandler]) =>
            String(result?.status) === conditionType
        ))?.[1] ??
      _localHandlers['defaultErrorCondition'] ??
      getGlobalRequestHandlers()['defaultErrorCondition'];
    const handledValue = handler(result.body);

    afterErrorHandler?.(result.header);

    return {
      body: handledValue as ResponseWithBody<200, void>,
      header: result.header,
      hasError: true,
    };
  }

  public replaceSigningCertificateURL(
    parameters: {
      file: {};
      userId: string;
    } & CommonRequestOptions
  ): string {
    let queryParameters: QueryParameters = {};
    const domain = parameters.$domain ? parameters.$domain : this.domain;
    let path = '/document-management/users/{userId}/certificate/';
    if (parameters.$path) {
      path =
        typeof parameters.$path === 'function'
          ? parameters.$path(path)
          : parameters.$path;
    }

    path = path.replace(
      '{userId}',
      `${encodeURIComponent(
        Backend.convertParameterCollectionFormat(
          parameters['userId'],
          ''
        ).toString()
      )}`
    );

    if (parameters.$queryParameters) {
      queryParameters = {...queryParameters, ...parameters.$queryParameters};
    }

    let keys = Object.keys(queryParameters);
    return (
      domain +
      path +
      (keys.length > 0
        ? '?' +
          keys
            .map((key) =>
              Array.isArray(queryParameters[key]) &&
              queryParameters[key].length >= 2
                ? queryParameters[key]
                    .map((value: any) => key + '=' + encodeURIComponent(value))
                    .join('&')
                : key + '=' + encodeURIComponent(queryParameters[key])
            )
            .join('&')
        : '')
    );
  }
}

const backend = new Backend(
  process.env.REACT_APP_SERVER_URL || '/api',
  (error) => {
    if (error.status === 401) {
      window.location.href =
        '/login/?redirect=' +
        encodeURIComponent(
          window.location.pathname +
            window.location.search +
            window.location.hash
        );
    }
  }
);

backend.setConfigureRequestHandler(
  (request: SuperAgentRequest): SuperAgentRequest => {
    if (new RegExp('/download', 'u').test(request.url)) {
      request.responseType('blob');
    }

    return request;
  }
);

export default backend;
